import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import io from 'socket.io-client';
import { API_URL, SOCKET_URL } from '../utils/api';
import { formatParisDateTime } from '../utils/parisTime';
import './TrackingPage.css';

const STATUS_STEPS = [
  { key: 'pending', label: 'Reçue', icon: '📥' },
  { key: 'preparing', label: 'En préparation', icon: '👨‍🍳' },
  { key: 'ready', label: 'Prête', icon: '✅' },
  { key: 'completed', label: 'Récupérée', icon: '🎉' }
];

const STEP_INDEX = { pending: 0, preparing: 1, ready: 2, completed: 3 };

function TrackingPage() {
  const { token } = useParams();
  const location = useLocation();
  const merci = location.state?.merci;
  const pointsToEarn = location.state?.pointsToEarn || 0;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(`${API_URL}/orders/track/${token}`);
        if (!response.ok) throw new Error('Commande introuvable');
        setOrder(await response.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Mises à jour de statut en temps réel
  useEffect(() => {
    if (!order) return;
    const socket = io(SOCKET_URL);
    socket.on('order-updated', (updated) => {
      if (updated.id === order.id) setOrder(prev => ({ ...prev, ...updated }));
    });
    return () => socket.close();
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="container-page tracking-chargement">
        <div className="skeleton" style={{ height: 300, maxWidth: 560, margin: '50px auto' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-page tracking-erreur">
        <h1>Commande introuvable</h1>
        <p>Le lien de suivi semble invalide ou expiré.</p>
        <Link to="/" className="btn btn-or">Retour à la carte</Link>
      </div>
    );
  }

  const stepIndex = STEP_INDEX[order.status] ?? 0;
  const cancelled = order.status === 'cancelled';

  return (
    <div className="container-page tracking-page">
      {merci && (
        <div className="tracking-merci">
          <h1>Merci {order.customerName} ! 🙌</h1>
          <p>
            Votre commande est bien enregistrée — gardez cette page pour suivre
            sa préparation en direct.
            {pointsToEarn > 0 && (
              <> Vous gagnerez <strong>{pointsToEarn} points</strong> au retrait.</>
            )}
          </p>
        </div>
      )}

      <div className="card tracking-carte">
        <div className="tracking-entete">
          <div>
            <p className="tracking-numero">Commande #{order.id}</p>
            <h2>Suivi en temps réel</h2>
          </div>
          {cancelled
            ? <span className="pill pill-rouge">Annulée</span>
            : <span className="pill pill-or">{STATUS_STEPS[stepIndex].label}</span>}
        </div>

        {!cancelled && (
          <div className="tracking-etapes">
            {STATUS_STEPS.map((step, i) => (
              <React.Fragment key={step.key}>
                {i > 0 && <div className={`etape-ligne ${i <= stepIndex ? 'faite' : ''}`} />}
                <div className={`etape ${i <= stepIndex ? 'faite' : ''} ${i === stepIndex ? 'courante' : ''}`}>
                  <div className="etape-rond">{step.icon}</div>
                  <span className="etape-label">{step.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {order.status === 'ready' && (
          <div className="tracking-prete">
            🎉 Votre commande est prête — vous pouvez venir la récupérer !
          </div>
        )}

        <div className="tracking-infos">
          <div className="tracking-info">
            <span>Retrait</span>
            <strong>{formatParisDateTime(order.pickupTime)}</strong>
          </div>
          <div className="tracking-info">
            <span>Au nom de</span>
            <strong>{order.customerName}</strong>
          </div>
          <div className="tracking-info">
            <span>Total</span>
            <strong>{parseFloat(order.total).toFixed(2)}€</strong>
          </div>
        </div>

        <div className="tracking-articles">
          <h3>Votre commande</h3>
          {(order.items || []).filter(Boolean).map((item, idx) => (
            <div key={idx} className="tracking-article">
              <div className="tracking-article-ligne">
                <span>{item.name} ×{item.quantity}</span>
                <span>{(parseFloat(item.price) * item.quantity).toFixed(2)}€</span>
              </div>
              {item.notes && <div className="tracking-article-notes">💬 {item.notes}</div>}
            </div>
          ))}
          {parseFloat(order.discount || 0) > 0 && (
            <div className="tracking-article-ligne tracking-reduction">
              <span>⭐ Article(s) offert(s)</span>
              <span>−{parseFloat(order.discount).toFixed(2)}€</span>
            </div>
          )}
        </div>

        <div className="tracking-pied">
          <Link to="/" className="btn btn-ghost">← Retour à la carte</Link>
          <a href="tel:0749110186" className="btn btn-ghost-or">☎ Nous appeler</a>
        </div>
      </div>
    </div>
  );
}

export default TrackingPage;
